/**
 * Profile editor.
 *
 * Name, picture, role and a short description, in one dialog rather than a
 * settings page: it is opened from the avatar in the top bar and is a handful
 * of fields, so sending the user to another route and back costs more than it
 * gives.
 *
 * The picture and the text fields go to two different endpoints, but they are
 * saved by one button — a "Save" that left the newly chosen photo unsent would
 * be a trap. The upload runs first, because it is the call that can fail on
 * size or type, and failing it before writing the text leaves the profile
 * unchanged rather than half-applied.
 */
import { useEffect, useRef, useState } from 'react';
import { Avatar, Modal } from './ui';
import { IconAuthError, IconDelete, IconSpinner } from './icons';
import { avatarSrc } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { cn } from '../utils/cn';

const BIO_LIMIT = 2000;
const NAME_LIMIT = 255;
const TITLE_LIMIT = 120;
const MAX_BYTES = 4 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="mb-1.5 flex items-baseline justify-between">
      <span className="text-[13px] font-medium text-gray-700">{label}</span>
      {hint && <span className="text-xs tabular-nums text-gray-400">{hint}</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 ' +
  'outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

const ProfileDialog = ({ open, onClose }) => {
  const { user, updateProfile, uploadAvatar } = useAuth();
  const fileRef = useRef(null);

  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Latest user, without making the reset below depend on it.
  const userRef = useRef(user);
  userRef.current = user;

  // Seed from whatever the server holds, once per opening, so a cancelled edit
  // doesn't linger into the next one.
  //
  // Keyed on `open` alone. With `user` in the dependencies this re-ran the
  // moment the avatar upload put a new user object into context — mid-save,
  // while the dialog was still open — and wiped the name, role and bio the
  // person had just typed back to the stored values.
  useEffect(() => {
    if (!open) return;
    const current = userRef.current;
    setFullName(current?.full_name || '');
    setTitle(current?.title || '');
    setBio(current?.bio || '');
    setPendingFile(null);
    setPreview(null);
    setError('');
  }, [open]);

  // A blob: URL is a document-lifetime reference; without this every photo
  // picked leaks its object until the tab closes.
  useEffect(() => {
    if (!pendingFile) return undefined;
    const url = URL.createObjectURL(pendingFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const pickFile = (event) => {
    const file = event.target.files?.[0];
    // Let the same file be chosen again after a failure: without this the
    // input's value is unchanged and onChange never fires a second time.
    event.target.value = '';
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setError('Choose a PNG, JPEG, WebP or GIF image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`That image is ${(file.size / 1048576).toFixed(1)}MB. The limit is 4MB.`);
      return;
    }
    setError('');
    setPendingFile(file);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const name = fullName.trim();
    if (!name) {
      setError('Your name cannot be empty.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (pendingFile) await uploadAvatar(pendingFile);
      await updateProfile({ full_name: name, title: title.trim(), bio: bio.trim() });
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || 'Could not save your profile. Try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const shownAvatar = preview || avatarSrc(user?.avatar_url);

  return (
    <Modal open={open} onClose={onClose} title="Your profile" description={user?.email}>
      <form onSubmit={handleSave} className="space-y-5 py-3">
        <div className="flex items-center gap-4">
          <Avatar src={shownAvatar} name={fullName || user?.full_name} size={64} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={saving}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:opacity-60"
              >
                {shownAvatar ? 'Change photo' : 'Upload photo'}
              </button>
              {pendingFile && (
                <button
                  type="button"
                  onClick={() => setPendingFile(null)}
                  disabled={saving}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[13px] text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900"
                >
                  <IconDelete size={14} aria-hidden="true" />
                  Discard
                </button>
              )}
            </div>
            <p className="mt-1.5 text-xs text-gray-400">PNG, JPEG, WebP or GIF, up to 4MB.</p>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED.join(',')}
              onChange={pickFile}
              className="hidden"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <IconAuthError size={16} className="mt-0.5 flex-shrink-0 text-red-500" aria-hidden="true" />
            <p className="text-[13px] text-red-700">{error}</p>
          </div>
        )}

        <Field label="Full name">
          <input
            type="text"
            value={fullName}
            maxLength={NAME_LIMIT}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Dr. Ama Boateng"
            disabled={saving}
            className={inputCls}
          />
        </Field>

        <Field label="Role">
          <input
            type="text"
            value={title}
            maxLength={TITLE_LIMIT}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Lead Clinician"
            disabled={saving}
            className={inputCls}
          />
        </Field>

        <Field label="About" hint={`${bio.length} / ${BIO_LIMIT}`}>
          <textarea
            rows={4}
            value={bio}
            maxLength={BIO_LIMIT}
            onChange={(e) => setBio(e.target.value)}
            placeholder="What you do, and which programmes you run."
            disabled={saving}
            className={cn(inputCls, 'resize-y')}
          />
        </Field>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && <IconSpinner size={15} className="animate-spin" aria-hidden="true" />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ProfileDialog;
